import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute, Router } from '@angular/router';
import { JoinService } from '../../../services/join/join.service';
import { GrowlerService } from '../../../services/growler.service';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogSizes } from '../../../enums/mat-dialog-sizes.enum';
import { TermsOfServiceDialogComponent } from '../../shared/terms-of-service-dialog/terms-of-service-dialog.component';
import { PrivacyPolicyDialogComponent } from '../../shared/privacy-policy-dialog/privacy-policy-dialog.component';
import moment from 'moment-timezone';
import environment from '../../../../environments/environment';
import { MatStepper } from '@angular/material/stepper';
import { SignupSteps } from '../../../enums/signup-steps.enum';
import { SignupRoles } from '../../../enums/signup-roles.enum';
import { PlaceResult } from '../../../services/geolocate.service';
import { GeoRegion } from '../../shared/geo-region-selector/geo-region-selector.component';
import { PHONE_VALIDATION_MASK } from '../../../directives/validators/string/phone-validator.directive';
import { CA_POSTAL_CODE_VALIDATION_MASK, MX_POSTAL_CODE_VALIDATION_MASK, US_POSTAL_CODE_VALIDATION_MASK } from '../../../directives/validators/string/postal-code-validator.directive';
import { filter, of, Subject } from 'rxjs';
import { HostListenerService } from '../../../services/host-listener.service';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { MaterialSearchComponent } from '../../shared/material-search/material-search.component';
import { RecaptchaComponent } from 'ng-recaptcha';
import { SignupJoinRequest } from '../../../models/access/signup/signup-join-request.model';
import { SignupRegistrationRequest } from '../../../models/access/signup/signup-registration-request.model';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { AuthService } from '../../../services/auth/auth.service';
import { GoogleAdWordsService } from '../../../services/google-ad-words.service';
import { PixelService } from 'ngx-pixel';
import { GtmService, RegistrationTypes } from '../../../services/gtm.service';
import { SignupOrganization } from './sign-up-organization.resolver';
import { ScrollService } from '../../../services/scroll.service';

@UntilDestroy()
@Component({
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['../auth-layout-shared-styles.scss'],
})
export class SignUpComponent implements OnInit
{
    readonly environment = environment;

    @ViewChild(MatStepper) signupStepper: MatStepper;

    // email input to focus when step loads
    @ViewChild('emailInput') emailInputRef: ElementRef;
    // user's full name input to focus when step loads
    @ViewChild('fullNameInput') fullNameInputRef: ElementRef;
    // group name input to focus when step loads
    @ViewChild('groupNameInput') groupNameInputRef: ElementRef;
    // org search to focus when step loads
    @ViewChild('organizationSearch') organizationSearchRef: MaterialSearchComponent;
    // org-search results container
    @ViewChild('hoaResults') hoaResultsRef: ElementRef;
    // address-search autocomplete input
    @ViewChild('addressSearch') addressSearchRef: ElementRef;
    // recaptcha input
    @ViewChild('captcha') recaptchaRef: RecaptchaComponent;

    readonly payHoaLogo = '/assets/images/payhoa/hoa-management-software/payhoa-logo.svg';
    readonly payHoaLink = 'https://www.payhoa.com';
    readonly loginRoute = '/auth/login';
    readonly defaultValidationIcon = 'fa fa-times text-danger mr-2';
    readonly siteKey = environment.RecaptchaSiteKey;
    readonly phoneNumberValidationMask = PHONE_VALIDATION_MASK;
    readonly year = moment().year();

    // Given to us prior to loading component (route or query params)
    signupOrganization: SignupOrganization;
    signupEmail: { email: string; verified: boolean; };
    landingPageConversion = false;

    // Form
    signupForm: FormGroup;
    signupFormDefaultValues = {
        role: '',
        email: '',
        group: {
            id: '',
            name: '',
        },
        groupName: '',
        fullName: '',
        address: '',
        phone: '',
        password: '',
        captcha: '',
    };
    addressForm: FormGroup;
    addressFormDefaultValues = {
        line1: '',
        line2: '',
        city: '',
        region: {
            countryCode: '',
            region: '',
        },
        postalCode: '',
    };
    addressPostalCodeInputMask: {
        mask: string;
        placeholder: string;
    } = {
        mask: US_POSTAL_CODE_VALIDATION_MASK,
        placeholder: US_POSTAL_CODE_VALIDATION_MASK.slice(0, US_POSTAL_CODE_VALIDATION_MASK.indexOf('||'))
                .replace(/0/g, '5'),
    };
    signupFormTypes = SignupRoles;
    signupSteps = SignupSteps;
    selectedStep = SignupSteps.SELECT_SIGNUP_TYPE;
    isSubmitting = false;
    isForwarding = false;

    brandedSignup = false;
    matMenuOpened = false;
    searchOrganizationsString = '';
    orgResults: {
        id: number;
        name: string;
    }[] = [];
    isSearchingOrganizations = false;

    availableUnitCounts: string[] = [
        '1 - 49',
        '50 - 149',
        '150 - 499',
        '500+',
    ];

    availableCommunityCounts: string[] = [
        '1 - 9',
        '10 - 24',
        '25 - 49',
        '50+',
    ];

    passwordValidations: { rule: string; text: string; icon: string; }[] = [
        {
            rule: 'minlength',
            text: 'Minimum of 8 Characters',
            icon: this.defaultValidationIcon,
        },
        {
            rule: 'upperCaseLetter',
            text: 'At least one uppercase character',
            icon: this.defaultValidationIcon,
        },
        {
            rule: 'lowerCaseLetter',
            text: 'At least one lowercase character',
            icon: this.defaultValidationIcon,
        },
        {
            rule: 'numericLetter',
            text: 'At least one numeric character',
            icon: this.defaultValidationIcon,
        },
    ];

    // track for hinter
    passwordHasFocus = false;
    passwordHide = true;

    // prevent multiple searches for HOAs
    private cancelPrevious: Subject<void> = new Subject();

    constructor(
            private _fb: FormBuilder,
            private _router: Router,
            private _dialog: MatDialog,
            private _joinService: JoinService,
            private _authService: AuthService,
            private _activatedRoute: ActivatedRoute,
            private _googleAdWordsService: GoogleAdWordsService,
            private _growler: GrowlerService,
            private _pixel: PixelService,
            private _gtm: GtmService,
            private _host: HostListenerService,
            private _scrollService: ScrollService,
    ) {
    }

    get loginForm(): FormGroup {
        return this.signupForm.get('login') as FormGroup;
    }

    ngOnInit() {
        if (LegFiJwtService.raw() === 'false') {
            LegFiJwtService.clear();
        }

        this.createForm();

        this._activatedRoute.data.pipe(untilDestroyed(this)).subscribe({
            next: (data) => {
                this.signupOrganization = data.organization;
                this.brandedSignup = !!this.signupOrganization.id;
                if (this.brandedSignup) {
                    this.signupForm.addControl('login', this._fb.group({}));
                    this.signupForm.patchValue({
                        group: {
                            id: this.signupOrganization.id,
                            name: this.signupOrganization.name,
                        },
                        groupName: this.signupOrganization.name,
                    });
                    this.selectAndProceed(SignupRoles.HOMEOWNER);
                    if (LegFiJwtService.read()) {
                        this.isForwarding = true;
                    }
                }
            },
        });

        this._host.onMouseClick().pipe(untilDestroyed(this)).subscribe({
            next: (event) => {
                if (this.matMenuOpened && !this.hoaResultsRef.nativeElement.contains(event.target)) {
                    this.matMenuOpened = false;
                }
            },
        });
    }

    selectAndProceed(role: SignupRoles) {
        this.signupForm.get('role').setValue(role);
        if (role === SignupRoles.HOMEOWNER) {
            this.selectedStep = SignupSteps.EMAIL_VERIFICATION;
        } else {
            this.updateIncludedFormControls(role);
            this.selectedStep = SignupSteps.ADD_SIGNUP_DETAILS;
        }
    }

    verifyEmail() {
        const control = this.signupForm.get('email');
        control.markAsTouched();
        control.updateValueAndValidity();

        if (control.valid) {
            this.isSubmitting = true;
            this.signupEmail = {
                email: control.value,
                verified: false,
            };
            this._joinService.checkEmail(control.value).pipe(
                    map(() => {
                        this.signupEmail.verified = true;
                        return true;
                    }),
                    catchError(() => {
                        return of(true);
                    }),
                    untilDestroyed(this),
            ).subscribe({
                next: () => {
                    this.signupForm.get('role').setValue(SignupRoles.HOMEOWNER);
                    this.updateIncludedFormControls(SignupRoles.HOMEOWNER);

                    if (this.signupEmail.verified) {
                        this.selectedStep = SignupSteps.CHECK_EMAIL;
                    } else {
                        this.selectedStep = this.signupOrganization.id
                                ? SignupSteps.ADD_SIGNUP_DETAILS
                                : SignupSteps.SEARCH_COMMUNITY;
                    }

                    this.isSubmitting = false;
                },
            });
        } else {
            this._growler.error('Error', 'Please provide an email address.');
        }
    }

    openIfResults() {
        if (this.searchOrganizationsString !== '') {
            this.matMenuOpened = true;
        }
    }

    onStepperAnimationDone() {
        this._scrollService.scrollToTop();

        switch (this.selectedStep) {
            case SignupSteps.ADD_SIGNUP_DETAILS:
                // always reset for password input
                this.passwordHide = true;
                this.passwordHasFocus = false;

                this.registrationStarted();

                if (this.signupForm.get('role').value === SignupRoles.HOMEOWNER) {
                    this.fullNameInputRef.nativeElement.focus();
                } else {
                    this.groupNameInputRef.nativeElement.focus();
                }
                break;
            case SignupSteps.SEARCH_COMMUNITY:
                this.organizationSearchRef.searchInput.nativeElement.focus();
                break;
            case SignupSteps.EMAIL_VERIFICATION:
                if (!this.brandedSignup) {
                    this.emailInputRef.nativeElement.focus();
                }
                break;
        }
    }

    onSearchOrganizations(term: string) {
        this.matMenuOpened = true;
        this.isSearchingOrganizations = false;

        if (term === this.searchOrganizationsString) {
            return;
        } else if (term === '') {
            this.orgResults = [];
            this.searchOrganizationsString = '';

            this.matMenuOpened = false;
            return;
        }

        this.searchOrganizationsString = term;
        this.cancelPrevious.next();

        if (this.searchOrganizationsString.length >= 5) {
            this.isSearchingOrganizations = true;
            this._joinService.searchOrg(this.searchOrganizationsString).pipe(
                    map((res: any[]) => res.map((org) => {
                        return {
                            id: org.id,
                            name: org.name,
                        };
                    })),
                    takeUntil(this.cancelPrevious),
                    untilDestroyed(this),
            ).subscribe({
                next: (organizations: { id: number; name: string; }[]) => {
                    this.orgResults = organizations;
                    this.isSearchingOrganizations = false;
                },
            });
        } else {
            this.orgResults = [];
            this.isSearchingOrganizations = false;
        }
    }

    onOrganizationSelected(org: { id: number; name: string; }) {
        this.signupForm.get('group').setValue(org);
        this.signupForm.get('groupName').setValue(org.name);
        this.selectedStep = SignupSteps.ADD_SIGNUP_DETAILS;
        this.organizationSearchRef.clearSearch();
    }

    onAddressAutocompleteSelected(value: PlaceResult) {
        const address = this.signupForm.get('address');
        address.reset(this.addressFormDefaultValues);

        const region: GeoRegion = {
            countryCode: '',
            region: '',
        };
        let postalCode = '';

        value.address_components.forEach((component) => {
            if (component.types.indexOf('locality') > -1) {
                address.get('city').setValue(component.long_name);
            }
            if (component.types.indexOf('administrative_area_level_1') > -1) {
                region.region = component.long_name;
            }
            if (component.types.indexOf('country') > -1) {
                region.countryCode = component.short_name;
            }
            if (component.types.indexOf('postal_code') > -1) {
                postalCode = component.long_name;
            }
            if (component.types.indexOf('postal_code_suffix') > -1) {
                postalCode += '-' + component.long_name;
            }
        });

        // Update formControl and force new value for autocomplete input
        address.get('line1').setValue(value.name);
        this.addressSearchRef.nativeElement.value = value.name;

        address.get('region').setValue(region);
        address.get('postalCode').setValue(postalCode);
    }

    isFieldValid(controlName: string) {
        if (this.signupForm) {
            const control = this.signupForm.get(controlName);
            if (control && !control.valid && control.touched) {
                // forcing new object (for now) to trigger ngOnChanges for stacked error messages
                return Object.assign({}, control) as AbstractControl;
            }
        }
    }

    updateValidation(controlName: string) {
        if (this.signupForm) {
            const control = this.signupForm.get(controlName);
            if (controlName === 'password') {
                this.passwordValidations = this.passwordValidations.map((validation) => {
                    const invalid = control.value === '' || (control.errors && control.errors.hasOwnProperty(validation.rule));
                    validation.icon = `fa ${invalid ? 'fa-times text-danger' : 'fa-check text-success'} mr-2`;
                    return validation;
                });
            }
        }
    }

    createAccount() {
        if (this.signupForm.valid) {
            this.isSubmitting = true;

            if (this.signupForm.get('role').value === SignupRoles.HOMEOWNER) {
                // JoinRequest
                const request = new SignupJoinRequest(this.signupForm.value);
                this._joinService.joinHoa(request).subscribe({
                    next: () => {
                        this.registrationCompleted(request.organizationId);
                        this.selectedStep = SignupSteps.JOIN_COMPLETE;
                        this.isSubmitting = false;
                    },
                    error: () => {
                        if (this.recaptchaRef) {
                            this.recaptchaRef.reset();
                        }
                        this.isSubmitting = false;
                        this._growler.error('Error', 'There was a problem sending the request to join.');
                    },
                });
            } else {
                this.signupEmail = {
                    email: this.signupForm.get('email').value,
                    verified: false,
                };
                this._joinService.checkEmail(this.signupEmail.email).pipe(
                        map(() => {
                            this.signupEmail.verified = true;
                            return true;
                        }),
                        catchError(() => {
                            return of(true);
                        }),
                        untilDestroyed(this),
                ).subscribe({
                    next: () => {
                        if (this.signupEmail.verified) {
                            this.selectedStep = SignupSteps.CHECK_EMAIL;
                        } else {
                            // proceed to new organization creation
                            this.createNewOrganization();
                        }
                    },
                });
            }
        } else if (this.selectedStep !== SignupSteps.EMAIL_VERIFICATION) {
            this.validateAllFormFields(this.signupForm);
            this._growler.error('Error', 'Please check all required fields.');
        }
    }

    resetForm() {
        this.updateIncludedFormControls();

        this.signupForm.reset(this.signupFormDefaultValues);
        this.addressForm.reset(this.addressFormDefaultValues);
        this.addressPostalCodeInputMask = {
            mask: US_POSTAL_CODE_VALIDATION_MASK,
            placeholder: US_POSTAL_CODE_VALIDATION_MASK.slice(0, US_POSTAL_CODE_VALIDATION_MASK.indexOf('||'))
                    .replace(/0/g, '5'),
        };
        this.updateValidation('password');

        this.selectedStep = SignupSteps.SELECT_SIGNUP_TYPE;
    }

    openTermsOfService() {
        this._dialog.open(TermsOfServiceDialogComponent, {
            width: MatDialogSizes.MD,
        });
    }

    openPrivacyPolicy() {
        this._dialog.open(PrivacyPolicyDialogComponent, {
            width: MatDialogSizes.MD,
        });
    }

    private createForm() {
        this.addressForm = this._fb.group({
            line1: [
                this.addressFormDefaultValues.line1,
                Validators.required,
            ],
            line2: this.addressFormDefaultValues.line2,
            city: [
                this.addressFormDefaultValues.city,
                Validators.required,
            ],
            region: [
                this.addressFormDefaultValues.region,
                Validators.required,
            ],
            postalCode: this.addressFormDefaultValues.postalCode, // using postal-code-validator.directive instead
        });

        this.signupForm = this._fb.group({
            role: this.signupFormDefaultValues.role,
            email: [
                this.signupFormDefaultValues.email,
                [
                    Validators.required,
                    Validators.email,
                ],
            ],
            fullName: [
                this.signupFormDefaultValues.fullName,
                Validators.required,
            ],
            address: this.addressForm,
            group: [
                this.signupFormDefaultValues.group,
                Validators.required,
            ],
            groupName: [
                this.signupFormDefaultValues.groupName,
                Validators.required,
            ],
            phone: this.signupFormDefaultValues.phone, // using phone-validator.directive instead
            password: this.signupFormDefaultValues.password, // using password-validator.directive instead
            captcha: [
                this.signupFormDefaultValues.captcha,
                Validators.required,
            ],
        });

        this.setFormHandlers();
    }

    private createNewOrganization() {
        // RegistrationRequest and Segmentation in one request!
        const request = new SignupRegistrationRequest(this.signupForm.value);
        this._authService.signup(request).pipe(untilDestroyed(this)).subscribe({
            next: (response: { orgId: number; token: string; }) => {
                this.registrationCompleted(response.orgId);

                // Store the user's JWT in local storage.
                if (response.token) {
                    LegFiJwtService.store(response.token);
                }

                if (environment.production) {
                    this._googleAdWordsService.setCapterraPixel();
                    this._pixel.track('CompleteRegistration');
                }

                this.isSubmitting = false;

                // noinspection JSIgnoredPromiseFromCall
                this._router.navigate(['/app/launchpad'], {queryParams: {first: true}});
            },
            error: () => {
                this.recaptchaRef.reset();
                this.isSubmitting = false;
                this._growler.error('Error', 'There was a problem sending the request to register your organization.');
            },
        });
    }

    private setFormHandlers() {
        // If region value is changed and is a different country code, go ahead and reset the postal code as the mask/validation will likely change
        this.addressForm.get('region').valueChanges.pipe(filter((value) => !!value), untilDestroyed(this)).subscribe({
            next: (value: GeoRegion) => {
                let mask;
                switch (value.countryCode) {
                    case 'MX':
                        mask = MX_POSTAL_CODE_VALIDATION_MASK;
                        break;
                    case 'CA':
                        mask = CA_POSTAL_CODE_VALIDATION_MASK;
                        break;
                    default:
                        // US, USA, CR all use US Mask
                        mask = US_POSTAL_CODE_VALIDATION_MASK;
                        break;
                }
                if (this.addressPostalCodeInputMask.mask !== mask) {
                    this.addressForm.get('postalCode').reset(this.addressFormDefaultValues.postalCode);

                    const placeholder = (mask.indexOf('||') > -1
                            ? mask.slice(0, mask.indexOf('||'))
                            : mask).replace(/0/g, '5');
                    this.addressPostalCodeInputMask = {
                        mask,
                        placeholder,
                    };
                }
            },
        });
    }

    private validateAllFormFields(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach((field) => {
            const control = formGroup.get(field);
            if (control instanceof FormControl) {
                control.markAsTouched({onlySelf: true});
            } else if (control instanceof FormGroup) {
                this.validateAllFormFields(control);
            }
        });
    }

    private updateIncludedFormControls(role: SignupRoles = null) {
        switch (role) {
            case SignupRoles.HOMEOWNER:
                this.signupForm.get('address').enable();
                this.signupForm.get('groupName').disable();
                this.signupForm.get('phone').disable();
                this.signupForm.get('password').disable();
                // disable from branded login form
                this.loginForm?.disable();
                break;
            case SignupRoles.BOARD_MEMBER:
            case SignupRoles.PROPERTY_MANAGER:
                this.signupForm.get('address').disable();
                this.signupForm.get('groupName').enable();
                this.signupForm.get('phone').enable();
                this.signupForm.get('password').enable();
                break;
            default:
                this.signupForm.get('address').enable();
                this.signupForm.get('groupName').enable();
                this.signupForm.get('phone').enable();
                this.signupForm.get('password').enable();
                break;
        }
    }

    private registrationStarted() {
        // once the email and role are established, we can set the registration type and trigger the custom event for new registrations
        this._gtm.registrationStart({
            type: this.signupForm.get('role').value === SignupRoles.HOMEOWNER
                    ? RegistrationTypes.JOIN
                    : RegistrationTypes.CREATE,
            landingPageConversion: this.landingPageConversion,
            email: this.signupForm.get('email').value,
            orgId: this.signupForm.get('group').value?.id,
        });
    }

    private registrationCompleted(orgId: number) {
        this._gtm.registrationComplete({
            type: this.signupForm.get('role').value === SignupRoles.HOMEOWNER
                    ? RegistrationTypes.JOIN
                    : RegistrationTypes.CREATE,
            landingPageConversion: this.landingPageConversion,
            email: this.signupForm.get('email').value,
            orgId,
        });
    }
}
